import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";
import "@/index.css";
import { loadDynamicExperiences } from "@/experiences/dynamicStore";

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

const params = new URLSearchParams(window.location.search);
const expParam = params.get("exp");
const kParam = params.get("k"); // legacy share link: experience passcode embedded
const shareParam = params.get("share"); // new share link: prompt for the passcode
const builderParam = params.get("builder"); // World Builder mode: ?exp=<id>&builder=1
const embedParam = params.get("_embed"); // World Builder iframe embed: ?exp=<id>&share=1&_embed=1
const clientPortalParam = params.get("clientportal"); // Client-facing portal: ?clientportal=<token>

async function verifyExperience(id, passcode) {
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope: "experience", id, passcode }),
    });
    const data = await res.json();
    return !!data.ok;
  } catch (e) {
    return false;
  }
}

function renderPortal(shareExpId) {
  import("@/components/Portal").then(({ default: Portal }) => {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <Portal shareExpId={shareExpId} />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  });
}

function renderDashboard() {
  import("@/components/Dashboard").then(({ default: Dashboard }) => {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <Dashboard />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  });
}

function renderWorldBuilder(worldId) {
  import("@/components/WorldBuilder").then(({ default: WorldBuilder }) => {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <WorldBuilder worldId={worldId} />
          <Toaster position="top-center" theme="dark" />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  });
}

function renderTemplateOverview(templateId) {
  import("@/components/TemplateOverview").then(({ default: TemplateOverview }) => {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <TemplateOverview templateId={templateId} />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  });
}

function renderAnalytics() {
  import("@/components/Analytics").then(({ default: AnalyticsDashboard }) => {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <AnalyticsDashboard />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  });
}

function renderClientPortal(token) {
  import("@/components/ClientPortalPage").then(({ default: ClientPortalPage }) => {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <ClientPortalPage token={token} />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  });
}

function renderExperience(NS_PREFIX) {
  // Patch localStorage with the experience namespace, hydrate server layout, THEN
  // import the deck so every module reads the right (namespaced) edits at import.
  Promise.all([import("@/lib/layoutSync"), import("@/App"), import("@/context/Experience")]).then(
    ([layoutSync, { default: App }, { ExperienceProvider }]) => {
      layoutSync.initLayoutSync(NS_PREFIX);
      layoutSync.hydrateFromServer().finally(() => {
        root.render(
          <React.StrictMode>
            <QueryClientProvider client={queryClient}>
              <ExperienceProvider>
                <App />
              </ExperienceProvider>
            </QueryClientProvider>
          </React.StrictMode>,
        );
      });
    },
  );
}

async function resolveDomainHost() {
  const h = window.location.hostname;
  if (!h || h === "localhost" || h.endsWith(".preview.emergentagent.com") || h.endsWith(".emergentagent.com")) return null;
  // Cache in sessionStorage — skip the API round-trip on every subsequent navigation
  const cacheKey = `__domain_exp:${h}`;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached !== null) return cached || null;
  try {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/domains/resolve?host=${encodeURIComponent(h)}`);
    if (!res.ok) { sessionStorage.setItem(cacheKey, ""); return null; }
    const data = await res.json();
    const expId = data.experienceId || null;
    sessionStorage.setItem(cacheKey, expId || "");
    return expId;
  } catch { sessionStorage.setItem(cacheKey, ""); return null; }
}

async function boot() {
  // Run domain resolution and experience hydration in parallel
  const [domainExpId] = await Promise.all([
    resolveDomainHost(),
    loadDynamicExperiences(),
  ]);

  if (domainExpId) {
    // Auto-unlock experience for domain visitors (domain itself acts as access control)
    sessionStorage.setItem("__expok:" + domainExpId, "1");
    // Patch URL params so active.js resolves the right experience
    const url = new URL(window.location.href);
    if (!url.searchParams.get("exp")) { url.searchParams.set("exp", domainExpId); window.history.replaceState({}, "", url); }
  }

  const { EXP_ID, NS_PREFIX } = await import("@/experiences/active");

  // When ?exp is omitted, EXP_ID resolves to the default experience (cresa) — so a
  // clean "?share=1" link opens Cresa without exposing the id in the URL.
  const targetExp = expParam || EXP_ID;
  const expUnlocked = sessionStorage.getItem("__expok:" + targetExp) === "1";

  // ── World Builder takes priority over everything else ──────────────
  if (expParam && builderParam === "1") {
    return renderWorldBuilder(expParam);
  }

  // ── Explicit dashboard navigation (?nav=...) always renders Dashboard ──
  if (params.get("nav")) {
    return renderDashboard();
  }

  // ── Client-facing portal link ───────────────────────────────────────
  if (clientPortalParam) {
    return renderClientPortal(clientPortalParam);
  }

  if (EXP_ID === targetExp) {
    if (expUnlocked) return renderExperience(NS_PREFIX);
    if (shareParam) {
      sessionStorage.setItem("__expok:" + targetExp, "1");
      return renderExperience(NS_PREFIX);
    }
    if (kParam) {
      const ok = await verifyExperience(targetExp, kParam);
      if (ok) {
        sessionStorage.setItem("__expok:" + targetExp, "1");
        sessionStorage.setItem("__expcode:" + targetExp, kParam);
        return renderExperience(NS_PREFIX);
      }
    }
  }
  // World Builder iframe embed mode — auto-authenticate without portal gate
  if (expParam && embedParam === "1") {
    sessionStorage.setItem("__expok:" + targetExp, "1");
    return renderExperience(NS_PREFIX);
  }
  // Default: show Dashboard (template gallery). Portal accessible at ?portal=1
  const templateParam = params.get("template");
  const viewParam = params.get("view");
  if (viewParam === "analytics") {
    renderAnalytics();
  } else if (templateParam) {
    renderTemplateOverview(templateParam);
  } else if (params.get("portal") === "1") {
    renderPortal();
  } else {
    renderDashboard();
  }
}

boot();
